import axios from 'axios';

// Use runtime config (window.ENV) with fallback to build-time env, then default
const API_BASE_URL =
  (window.ENV && window.ENV.API_URL !== 'REPLACE_AT_RUNTIME' ? window.ENV.API_URL : null) ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear it and redirect to login
      localStorage.removeItem('jwt_token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const clientsApi = {
  getAll: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.search) queryParams.append('search', params.search);
    return api.get(`/clients?${queryParams.toString()}`);
  },
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.patch(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  updateWorkflowStep: (id, stepName, completed) =>
    api.put(`/clients/${id}/workflow/${stepName}`, { completed }),
  exportToExcel: () => api.get('/clients/export/excel', { responseType: 'blob' }),
};

export const workflowApi = {
  getAll: () => api.get('/workflow'),
  create: (data) => api.post('/workflow', data),
  update: (id, data) => api.put(`/workflow/${id}`, data),
  delete: (id) => api.delete(`/workflow/${id}`),
  reorder: (steps) => {
    // Backend expects { steps: [{ id, order }] } format
    const formattedSteps = steps
      .filter(step => step._id || step.id) // Filter out any steps without ID
      .map((step, index) => ({
        id: step._id || step.id,
        order: index + 1,
      }));
    console.log('Reordering steps:', formattedSteps);
    return api.put('/workflow/reorder/all', { steps: formattedSteps });
  },
  sync: () => api.post('/workflow/sync'),
};

export const settingsApi = {
  getAll: () => api.get('/settings'),
  getSetting: (key) => api.get(`/settings/${key}`),
  updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
  getDropdownOptions: (type) => api.get(`/settings/dropdown/${type}`),
  updateDropdownOptions: (type, options) => api.put(`/settings/dropdown/${type}`, { options }),
};

export const authApi = {
  login: (entraId, email, azureToken) => {
    const headers = azureToken ? { 'X-Azure-Token': azureToken } : {};
    return api.post('/auth/login', { entraId, email }, { headers });
  },
};

export const usersApi = {
  syncUser: (data, azureToken) => {
    const headers = azureToken ? { 'X-Azure-Token': azureToken } : {};
    return api.post('/users/sync', data, { headers });
  },
  getAll: (params) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.active !== undefined) queryParams.append('active', params.active);
    return api.get(`/users?${queryParams.toString()}`);
  },
  getStatistics: () => api.get('/users/statistics'),
  getOne: (id) => api.get(`/users/${id}`),
  getByEntraId: (entraId) => api.get(`/users/entra/${entraId}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  activate: (id) => api.post(`/users/${id}/activate`),
  delete: (id) => api.delete(`/users/${id}`),
};

export default api;
